import { useState } from "react";
import { toast } from "sonner";
import {
  BadgeCheck,
  Building2,
  Check,
  Copy,
  CreditCard,
  Download,
  Hash,
  Landmark,
  MapPin,
  QrCode,
  ShieldCheck,
  Smartphone,
  User,
  ZoomIn,
  type LucideIcon,
} from "lucide-react";
import { Reveal } from "@project/user/components/motion";
import { Button } from "@project/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";

export interface BankDetailsConfig {
  /** Display name of the organisation that owns the account */
  orgName: string;
  bankName: string;
  branch: string;
  accountNumber: string;
  accountName: string;
  ifsc: string;
  pan?: string;
  /** e.g. "Trust registered under 80-G of Income Tax Act" */
  taxNote?: string;
  upiId: string;
  /** Payee name used in the UPI deep link */
  upiPayeeName: string;
  qrImage: string;
  qrDownloadName: string;
}

function useCopy() {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const copy = async (key: string, value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      toast.success(`${label} copied`);
      window.setTimeout(
        () => setCopiedKey(current => (current === key ? null : current)),
        1800
      );
    } catch {
      toast.error("Unable to copy. Please copy manually.");
    }
  };

  return { copiedKey, copy };
}

function CopyButton({
  copied,
  label,
  onClick,
}: {
  copied: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Copy ${label}`}
      className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg border transition-all ${copied
        ? "border-emerald-500 bg-emerald-50 text-emerald-600"
        : "border-border bg-card text-muted-foreground hover:border-primary hover:bg-primary hover:text-primary-foreground"
        }`}
    >
      {copied ? (
        <Check className="h-4 w-4" strokeWidth={2.5} />
      ) : (
        <Copy className="h-4 w-4" />
      )}
    </button>
  );
}

/** Small info tile used for bank / branch / name / IFSC */
function DetailTile({
  icon: Icon,
  label,
  value,
  mono,
  copied,
  onCopy,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  mono?: boolean;
  copied?: boolean;
  onCopy?: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 transition-all duration-300 hover:border-primary/40 hover:shadow-md">
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-secondary text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        <p
          className={`mt-0.5 break-words text-[0.95rem] font-semibold text-foreground ${mono ? "font-mono tracking-wider" : ""
            }`}
        >
          {value}
        </p>
      </div>
      {onCopy && (
        <CopyButton copied={!!copied} label={label} onClick={onCopy} />
      )}
    </div>
  );
}

/** Bank transfer details (left) + UPI QR code (right) */
export function BankDetailsSection({
  id,
  details,
}: {
  id: string;
  details: BankDetailsConfig;
}) {
  const { copiedKey, copy } = useCopy();
  const [qrOpen, setQrOpen] = useState(false);

  const upiPayLink = `upi://pay?pa=${details.upiId}&pn=${encodeURIComponent(
    details.upiPayeeName
  )}&cu=INR`;

  const allDetails = [
    `Bank: ${details.bankName}`,
    `Branch: ${details.branch}`,
    `A/C No: ${details.accountNumber}`,
    `A/C Name: ${details.accountName}`,
    `IFSC Code: ${details.ifsc}`,
    details.pan && `PAN: ${details.pan}`,
    `UPI ID: ${details.upiId}`,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <section
      id={id}
      className="border-border/40 bg-secondary/30 relative overflow-hidden border-t py-14 sm:py-20"
    >
      {/* soft decorative blobs */}
      <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-[#E38018]/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />

      <div className="relative mx-auto max-w-6xl px-4">
        <Reveal variant="morph" className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#E38018]">
            Donate Directly
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground sm:text-4xl">
            Bank Transfer & UPI Details
          </h2>
          <p className="mx-auto mt-1 max-w-2xl text-sm text-muted-foreground">
            Deposit your contribution directly into our bank account or scan
            the UPI QR code to donate instantly.
          </p>
        </Reveal>

        <div className="mt-10 grid items-stretch gap-6 lg:grid-cols-[1.4fr_1fr]">
          {/* Left: Bank details */}
          <Reveal variant="left" delay={100} className="h-full">
            <div className="flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
              {/* Header */}
              <div className="relative overflow-hidden bg-primary px-6 py-3 text-primary-foreground sm:px-8">
                <Landmark className="absolute -right-2 -top-2 h-16 w-16 rotate-12 opacity-10" />
                <div className="relative flex items-center justify-center gap-2">
                  <div className="grid h-8 w-8 place-items-center  ">
                    <Landmark className="h-5 w-5" />
                  </div>
                  <h3 className="!font-sans !text-sm !font-bold uppercase !tracking-[0.18em] !text-white">
                    Bank Account Details
                  </h3>
                </div>
              </div>

              <div className="flex flex-1 flex-col gap-4 p-5 sm:p-6">
                {/* Highlighted account number */}
                <div className="rounded-2xl border border-dashed border-[#E38018]/50 bg-[#fff8ef] px-5 py-4">
                  <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-[#E38018]">
                    <Hash className="h-3.5 w-3.5" />
                    Account Number
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <p className="font-mono !text-xl font-bold tracking-[0.15em] text-foreground sm:text-3xl">
                      {details.accountNumber}
                    </p>
                    <CopyButton
                      copied={copiedKey === "account"}
                      label="Account number"
                      onClick={() =>
                        copy("account", details.accountNumber, "Account number")
                      }
                    />
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <DetailTile
                    icon={User}
                    label="Account Name"
                    value={details.accountName}
                    copied={copiedKey === "name"}
                    onCopy={() =>
                      copy("name", details.accountName, "Account name")
                    }
                  />
                  <DetailTile
                    icon={Building2}
                    label="IFSC Code"
                    value={details.ifsc}
                    mono
                    copied={copiedKey === "ifsc"}
                    onCopy={() => copy("ifsc", details.ifsc, "IFSC code")}
                  />
                  <DetailTile
                    icon={Landmark}
                    label="Bank"
                    value={details.bankName}
                  />
                  <DetailTile
                    icon={MapPin}
                    label="Branch"
                    value={details.branch}
                  />
                  {details.pan && (
                    <DetailTile
                      icon={CreditCard}
                      label="PAN Card Number"
                      value={details.pan}
                      mono
                      copied={copiedKey === "pan"}
                      onCopy={() => copy("pan", details.pan!, "PAN")}
                    />
                  )}
                  {details.taxNote && (
                    <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-emerald-100 text-emerald-700">
                        <BadgeCheck className="h-5 w-5" />
                      </div>
                      <p className="text-[0.85rem] font-semibold leading-snug text-emerald-800">
                        {details.taxNote}
                      </p>
                    </div>
                  )}
                </div>

                <div className="mt-auto flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
                  <p className="flex items-center gap-2 text-xs text-muted-foreground">
                    <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600" />
                    Official account of {details.orgName}
                  </p>
                  <Button
                    type="button"
                    onClick={() => copy("all", allDetails, "Bank details")}
                    className="h-10 gap-2 rounded-xl bg-primary text-sm px-5 font-semibold text-primary-foreground hover:bg-primary/90"
                  >
                    {copiedKey === "all" ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                    Copy All Details
                  </Button>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Right: UPI QR */}
          <Reveal variant="right" delay={220} className="h-full">
            <div className="flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
              <div className="flex items-center justify-center gap-2 bg-[#E38018] px-6 py-4 text-white">
                <QrCode className="h-5 w-5" />
                <h3 className="!font-sans !text-sm !font-bold uppercase !tracking-[0.18em] !text-white">
                  Scan & Pay with UPI
                </h3>
              </div>

              <div className="flex flex-1 flex-col items-center p-6 text-center">
                {/* QR with scanner-style corners */}
                <button
                  type="button"
                  onClick={() => setQrOpen(true)}
                  aria-label="Enlarge UPI QR code"
                  className="group relative w-full max-w-[215px] p-3"
                >
                  <span className="absolute left-0 top-0 h-8 w-8 rounded-tl-xl border-l-4 border-t-4 border-[#E38018]" />
                  <span className="absolute right-0 top-0 h-8 w-8 rounded-tr-xl border-r-4 border-t-4 border-[#E38018]" />
                  <span className="absolute bottom-0 left-0 h-8 w-8 rounded-bl-xl border-b-4 border-l-4 border-[#E38018]" />
                  <span className="absolute bottom-0 right-0 h-8 w-8 rounded-br-xl border-b-4 border-r-4 border-[#E38018]" />
                  <div className="relative overflow-hidden rounded-lg bg-white p-2 shadow-sm transition-transform duration-300 group-hover:scale-[1.02]">
                    <img
                      src={details.qrImage}
                      alt={`${details.orgName} UPI QR code`}
                      loading="lazy"
                      className="aspect-square w-full object-contain"
                    />
                    <span className="absolute inset-0 grid place-items-center bg-black/0 opacity-0 transition-all group-hover:bg-black/40 group-hover:opacity-100">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-neutral-800">
                        <ZoomIn className="h-3.5 w-3.5" />
                        Tap to enlarge
                      </span>
                    </span>
                  </div>
                </button>

                <p className="mt-3 text-sm font-bold uppercase tracking-wide text-foreground">
                  {details.orgName}
                </p>

                <button
                  type="button"
                  onClick={() => copy("upi", details.upiId, "UPI ID")}
                  className="mt-2 inline-flex max-w-full items-center gap-2 rounded-full border border-border bg-secondary/50 px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary"
                >
                  <span className="text-black font-bold">UPI ID:</span>
                  <span className="truncate font-mono">{details.upiId}</span>
                  {copiedKey === "upi" ? (
                    <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                  ) : (
                    <Copy className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  )}
                </button>

                <div className="mt-auto grid w-full gap-2 pt-5 sm:grid-cols-2">
                  <Button
                    asChild
                    className="h-11 gap-2 rounded-xl bg-[#E38018] font-semibold text-white hover:bg-[#E38018]/90 md:hidden"
                  >
                    <a href={upiPayLink}>
                      <Smartphone className="h-4 w-4" />
                      Pay via UPI App
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="border-primary/30 h-10 gap-2 rounded-xl font-semibold text-primary hover:bg-primary hover:text-primary-foreground md:col-span-2"
                  >
                    <a href={details.qrImage} download={details.qrDownloadName}>
                      <Download className="h-4 w-4" />
                      Download QR
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <p className="mt-8 text-center text-sm text-muted-foreground">
          After transferring, please share the transaction details on{" "}
          <a
            href="tel:+919978015424"
            className="font-semibold text-primary hover:underline"
          >
            99780-15424
          </a>{" "}
          to receive your donation receipt.
        </p>
      </div>

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">Scan to Donate</DialogTitle>
            <DialogDescription className="text-center">
              Open any UPI app and scan this code to donate to{" "}
              {details.orgName}.
            </DialogDescription>
          </DialogHeader>
          <img
            src={details.qrImage}
            alt={`${details.orgName} UPI QR code`}
            className="anim-zoom-in aspect-square w-full rounded-xl border border-border bg-white object-contain p-3"
          />
          <p className="text-center font-mono text-xs text-muted-foreground">
            {details.upiId}
          </p>
        </DialogContent>
      </Dialog>
    </section>
  );
}
